/**
 * The top-of-page answer to "is the API alright?".
 *
 * Three facts, in the order someone actually asks for them: how much traffic
 * there was, how it split across status classes, and how many endpoints are in
 * each health state. Everything below it on the page is detail.
 *
 * The one thing this component refuses to do is round the uncomfortable parts
 * away. `unknown` gets a tile the same size as `healthy` even though it will
 * hold ~1,058 of the 1,062 endpoints, because "we have no idea about almost
 * everything" is the single most important caveat on the screen and hiding it
 * would turn a monitoring dashboard into a reassurance dashboard.
 */
import { useQuery } from '@tanstack/react-query'

import { ApiError, api } from '../api/client'
import { HEALTH_STATES, WINDOW_REFRESH_MS } from '../api/types'
import type { HealthState, OverviewResponse, WindowId } from '../api/types'
import { useI18n } from '../i18n'
import { FreshnessBadge } from './FreshnessBadge'
import { BUCKET_STYLES, HEALTH_STYLES, bucketTotals, numberFormat, percent } from './statusColors'

/** Worst first, so the eye lands on the states that need action. */
const TILE_ORDER: readonly HealthState[] = ['down', 'degraded', 'unknown', 'healthy']

export function overviewQueryKey(window: WindowId) {
  return ['overview', window] as const
}

export interface OverviewProps {
  window: WindowId
}

/**
 * The overview query, exported so the page shell can read the same cached
 * response for its filter chips instead of firing a second identical request.
 *
 * The polling policy lives here rather than in the global query defaults
 * because it is per-window: 30m data turns over every minute, 7d data every
 * hour, and one interval for both would either hammer the API or leave a tab
 * an hour behind.
 */
export function useOverviewQuery(window: WindowId) {
  return useQuery({
    queryKey: overviewQueryKey(window),
    queryFn: () => api.get<OverviewResponse>('/api/v1/overview', { window }),
    staleTime: WINDOW_REFRESH_MS[window],
    refetchInterval: WINDOW_REFRESH_MS[window],
    refetchOnWindowFocus: true,
  })
}

export function Overview({ window }: OverviewProps) {
  const { t } = useI18n()
  const { data, error, isPending } = useOverviewQuery(window)

  if (isPending) return <OverviewSkeleton />
  if (error) return <OverviewError error={error} />

  const buckets = bucketTotals(data.by_status)
  const total = data.total_requests

  return (
    <section aria-label={t('dashboard.overview.label')} className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-2">
          <span className="num text-3xl font-semibold text-fg">{numberFormat.format(total)}</span>
          <span className="text-sm text-muted">{t('dashboard.overview.requests')}</span>
        </div>
        <FreshnessBadge meta={data.meta} />
      </header>

      {total === 0 ? (
        // An all-grey full-width bar would claim a composition we do not have.
        <p className="rounded-md border border-border bg-hatch px-3 py-2 text-xs text-muted">
          {t('dashboard.overview.noRequests')}
        </p>
      ) : (
        <StatusBar byStatus={data.by_status} total={total} />
      )}

      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {TILE_ORDER.map((state) => (
          <HealthTile
            key={state}
            state={state}
            count={data.health_counts[state] ?? 0}
            total={countEndpoints(data.health_counts)}
          />
        ))}
      </ul>

      {buckets.length > 0 && <Legend buckets={buckets} total={total} />}
    </section>
  )
}

function countEndpoints(counts: Record<HealthState, number>): number {
  return HEALTH_STATES.reduce((sum, state) => sum + (counts[state] ?? 0), 0)
}

/* ------------------------------------------------------------------ */

function StatusBar({ byStatus, total }: { byStatus: Record<string, number>; total: number }) {
  const { t } = useI18n()
  const buckets = bucketTotals(byStatus)
  // One key for the whole sentence, one for each "<bucket> <share>" pair, and
  // the list separator itself is locale data — Chinese joins lists with 、
  const parts = buckets
    .map((b) =>
      t('dashboard.overview.compositionPart', {
        label: t(`dashboard.statusBucket.${b.bucket}`),
        share: percent(b.count / total),
      }),
    )
    .join(t('dashboard.overview.listSeparator'))

  return (
    <div
      data-testid="status-bar"
      role="img"
      aria-label={t('dashboard.overview.composition', { parts })}
      className="flex h-3 w-full overflow-hidden rounded-full bg-border"
    >
      {buckets.map(({ bucket, count }) => (
        <span
          key={bucket}
          // Decorative: the composition is already announced once on the parent,
          // and five more announcements would just be noise.
          role="presentation"
          data-bucket={bucket}
          title={t('dashboard.overview.bucketTooltip', {
            label: t(`dashboard.statusBucket.${bucket}`),
            count: numberFormat.format(count),
            share: percent(count / total),
          })}
          style={{ width: `${(count / total) * 100}%` }}
          className={BUCKET_STYLES[bucket].bg}
        />
      ))}
    </div>
  )
}

function Legend({
  buckets,
  total,
}: {
  buckets: { bucket: keyof typeof BUCKET_STYLES; count: number }[]
  total: number
}) {
  const { t } = useI18n()
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {buckets.map(({ bucket, count }) => (
        <li
          key={bucket}
          className="flex items-center gap-1.5"
          title={t(`dashboard.statusBucket.${bucket}`)}
        >
          <span aria-hidden="true" className={`h-2 w-2 rounded-sm ${BUCKET_STYLES[bucket].bg}`} />
          <span className="text-fg">{BUCKET_STYLES[bucket].label}</span>
          <span className="num">{numberFormat.format(count)}</span>
          <span className="num">({percent(count / total)})</span>
        </li>
      ))}
    </ul>
  )
}

function HealthTile({ state, count, total }: { state: HealthState; count: number; total: number }) {
  const { t } = useI18n()
  const style = HEALTH_STYLES[state]
  return (
    <li
      data-testid={`health-tile-${state}`}
      title={t(`dashboard.healthHint.${state}`)}
      className="rounded-md border border-border bg-surface p-3"
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted">
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${style.bg}`} />
        {t(`domain.health.${state}`)}
      </div>
      <div className="num mt-1 text-2xl font-semibold text-fg">{numberFormat.format(count)}</div>
      <div className="text-xs text-muted">
        {total > 0
          ? t('dashboard.overview.tileShare', {
              share: percent(count / total, 0),
              total: numberFormat.format(total),
            })
          : t('dashboard.overview.tileNoEndpoints')}
      </div>
    </li>
  )
}

function OverviewSkeleton() {
  const { t } = useI18n()
  return (
    <section aria-label={t('dashboard.overview.label')} aria-busy="true" className="space-y-4">
      <div className="h-8 w-40 animate-pulse rounded bg-border" />
      <div className="h-3 w-full animate-pulse rounded-full bg-border" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {TILE_ORDER.map((state) => (
          <div key={state} className="h-20 animate-pulse rounded-md bg-border" />
        ))}
      </div>
    </section>
  )
}

/**
 * A failed overview shows the failure, never a zeroed-out dashboard. The API
 * only 503s when it has *no* snapshot at all; anything it can serve comes back
 * with `degraded: true` instead, which the freshness badge reports.
 */
function OverviewError({ error }: { error: unknown }) {
  const { t } = useI18n()
  // `HTTP 503` is a protocol token, not prose: it reads the same in both
  // languages and is deliberately left untranslated.
  const detail =
    error instanceof ApiError
      ? error.detail || `HTTP ${error.status}`
      : t('dashboard.overview.unreachable')
  return (
    <section aria-label={t('dashboard.overview.label')}>
      <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
        {t('dashboard.overview.error', { detail })}
      </p>
    </section>
  )
}
